import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { JsonService } from '../../_services/json.service';
import { LoggerService } from '../../_services/logger.service';
import { Country } from '../../_models/country.model';
import { RelatorioIntervencoesRevisor } from '../../_models/relatorio-intervencoes.model';

@Component({
  selector: 'app-relatorio-intervencoes',
  templateUrl: './relatorio-intervencoes.component.html',
  styleUrls: ['./relatorio-intervencoes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelatorioIntervencoesComponent implements OnInit {

  filtro = { countryCode: '', ano: String(new Date().getFullYear()), mes: '', usuario: '' };

  listPaises: Country[] = [];
  listaUsuarios: string[] = [];

  dadosFiltrados: RelatorioIntervencoesRevisor[] = [];

  readonly listAnos: string[] = Array.from(
    { length: new Date().getFullYear() - 2022 },
    (_, i) => String(new Date().getFullYear() - i)
  );

  readonly listMeses = [
    { label: 'Janeiro',   value: '01_ENERO' },
    { label: 'Fevereiro', value: '02_FEBRERO' },
    { label: 'Março',     value: '03_MARZO' },
    { label: 'Abril',     value: '04_ABRIL' },
    { label: 'Maio',      value: '05_MAYO' },
    { label: 'Junho',     value: '06_JUNIO' },
    { label: 'Julho',     value: '07_JULIO' },
    { label: 'Agosto',    value: '08_AGOSTO' },
    { label: 'Setembro',  value: '09_SEPTIEMBRE' },
    { label: 'Outubro',   value: '10_OCTUBRE' },
    { label: 'Novembro',  value: '11_NOVIEMBRE' },
    { label: 'Dezembro',  value: '12_DICIEMBRE' },
  ];

  dados: RelatorioIntervencoesRevisor[] = [];
  isLoading    = false;
  isCarregando = false;
  semDados     = false;

  totais = this.totaisZerados();

  constructor(
    private jsonService: JsonService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.filtro.mes = this.listMeses[new Date().getMonth()].value;
    this.jsonService.listCountry().subscribe({
      next: paises => { this.listPaises = paises || []; this.cdr.markForCheck(); },
      error: erro  => { this.logger.error('Erro ao carregar países', erro); }
    });
  }

  buscar(): void {
    if (!this.filtro.countryCode || !this.filtro.ano || !this.filtro.mes) return;

    this.isLoading = true;
    this.semDados  = false;
    this.cdr.markForCheck();

    const params = new HttpParams()
      .set('countryCode', this.filtro.countryCode)
      .set('ano', this.filtro.ano)
      .set('mes', this.filtro.mes);

    this.jsonService.getDashboardRelatorioIntervencoes(params).subscribe({
      next: data => {
        this.dados         = (data as RelatorioIntervencoesRevisor[]) || [];
        this.listaUsuarios = [...new Set(this.dados.map(r => r.usuario).filter(Boolean))] as string[];
        this.filtrar();
        this.semDados  = this.dados.length === 0;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: erro => {
        this.logger.error('Erro ao carregar Relatório Interação Usuário', erro);
        this.semDados  = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  atualizarDados(): void {
    if (!this.filtro.countryCode || !this.filtro.ano || !this.filtro.mes) return;

    this.isCarregando = true;
    this.cdr.markForCheck();

    const params = new HttpParams()
      .set('countryCode', this.filtro.countryCode)
      .set('ano', this.filtro.ano)
      .set('mes', this.filtro.mes);

    this.jsonService.carregarRelatorioIntervencoes(params).subscribe({
      next: () => {
        this.isCarregando = false;
        this.buscar();
      },
      error: erro => {
        this.logger.error('Erro ao atualizar Relatório Interação Usuário', erro);
        this.isCarregando = false;
        this.cdr.markForCheck();
      }
    });
  }

  filtrar(): void {
    this.dadosFiltrados = this.filtro.usuario
      ? this.dados.filter(r => r.usuario === this.filtro.usuario)
      : this.dados;
    this.calcularTotais();
    this.cdr.markForCheck();
  }

  limpar(): void {
    this.filtro         = { countryCode: '', ano: String(new Date().getFullYear()), mes: this.listMeses[new Date().getMonth()].value, usuario: '' };
    this.dados          = [];
    this.dadosFiltrados = [];
    this.listaUsuarios  = [];
    this.semDados       = false;
    this.totais         = this.totaisZerados();
    this.cdr.markForCheck();
  }

  trackByRow(_: number, row: RelatorioIntervencoesRevisor): string {
    return row.usuario + row.interacaoUsuario;
  }

  trackByCountryCode(_: number, item: Country): string {
    return item.countryCode;
  }

  trackByIndex(index: number): number {
    return index;
  }

  exportarCsv(): void {
    const headers = ['Pais', 'Periodo', 'Usuario', 'InteracaoUsuario', 'Instituicao', 'Medico', 'Produto', 'TotalGeneral'];
    const linhas = this.dadosFiltrados.map(r => [
      r.pais, r.periodo, r.usuario, r.interacaoUsuario,
      r.instituicao, r.medico, r.produto, r.totalGeneral
    ].join(';'));

    const csv = [headers.join(';'), ...linhas].join('\n');
    const aaaamm = `${this.filtro.ano}${this.filtro.mes.substring(0, 2)}`;
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url  = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const ts = new Date(); const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
    link.setAttribute('download', `${aaaamm}–${this.filtro.countryCode}–interacciones_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private calcularTotais(): void {
    this.totais = {
      instituicao:  this.dadosFiltrados.reduce((acc, r) => acc + (Number(r.instituicao)  || 0), 0),
      medico:       this.dadosFiltrados.reduce((acc, r) => acc + (Number(r.medico)       || 0), 0),
      produto:      this.dadosFiltrados.reduce((acc, r) => acc + (Number(r.produto)      || 0), 0),
      totalGeneral: this.dadosFiltrados.reduce((acc, r) => acc + (Number(r.totalGeneral) || 0), 0),
    };
  }

  private totaisZerados() {
    return { instituicao: 0, medico: 0, produto: 0, totalGeneral: 0 };
  }
}
